import * as preact from "preact";

import * as analytics from "analytics";
import * as deviceDetect from "deviceDetect";
import * as experiment from "experiment";
import * as modalController from "modalController";
import readlangClient from "readlangClient";
import * as shareLink from "shareLink";
import stores from "stores/stores";
import * as upsellTimer from "upsellTimer";
import Dialog from "views/Dialog";
import SocialShareDialog from "views/SocialShareDialog";

/**
 * sharePrompt1 experiment. Control keeps the recurring "Spread the word" timer
 * modal. The experiment arm drops it and instead gets a rabbit share prompt at
 * most once a month, at a delight moment: a thumbs-up on the end-of-text vote,
 * finishing a practice session, or reaching a 7-day streak (held until the
 * next page change that isn't the reader, so never mid-reading). Signed-in
 * users without Premium are treated at whichever comes first, in both arms.
 * Signed-out visitors keep the timer modal and aren't treated. Not in the iOS
 * app, which asks for neither (an App Store review prompt at a happy moment
 * may follow there). See docs/experiments/2026-10-sharePrompt1.md.
 */

type DelightMoment = "upvote" | "practice" | "streak";

const SHARE_URL = "https://readlang.com/?ref=share";
const STREAK_DAYS = 7;

const HEADINGS: Record<DelightMoment, string> = {
  upvote: "Glad you enjoyed it!",
  practice: "Nice work!",
  streak: `${STREAK_DAYS} days in a row!`,
};

let pendingStreakMoment = false;

const isInExperimentPopulation = () =>
  !deviceDetect.isIOSApp() &&
  !stores.user.isNew.value &&
  !stores.user.isLocalMode.value &&
  !stores.user.hasPremiumAccess.value;

const isExperimentArm = () =>
  experiment.getConditionAndTreat("sharePrompt1") === "experiment";

class SharePromptDialog extends preact.Component<{ moment: DelightMoment }> {
  share = async () => {
    analytics.trackEventWithProperties("sharePromptShareTapped", {
      moment: this.props.moment,
      bySheet: shareLink.sharesBySheet(),
    });
    modalController.cancel(undefined, true);
    await shareLink.shareLink({ title: "Readlang", url: SHARE_URL });
  };

  render() {
    return (
      <Dialog class="sharePrompt">
        <img
          class="sharePromptRabbit"
          src="/content/landingPages/rabbit-welcome.webp"
          alt=""
          width="1254"
          height="1254"
        />
        <h2>{HEADINGS[this.props.moment]}</h2>
        <p>
          I'm Steve, and I make Readlang. Most learners find it because a friend
          told them about it. If it's helping you, would you share it with
          someone who's learning a language?
        </p>
        <button class="payButton" onClick={this.share}>
          Share Readlang
        </button>
        <button
          class="linkButton sharePromptLater"
          onClick={() => modalController.cancel()}
        >
          Maybe later
        </button>
      </Dialog>
    );
  }
}

const showAtDelightMoment = async (moment: DelightMoment) => {
  if (
    !isInExperimentPopulation() ||
    modalController.isShown() ||
    !isExperimentArm()
  ) {
    return;
  }
  // The server's once-a-month claim lets only one tab or device through
  const { data } = await readlangClient.request<{ show: boolean }>(
    "POST",
    "/api/user/claimSharePrompt",
    {}
  );
  if (!data.show) return;
  modalController.show(<SharePromptDialog moment={moment} />, {
    title: "sharePrompt",
    cancelCallback: () => {
      analytics.trackEventWithProperties("sharePromptDismissed", { moment });
    },
  });
  analytics.trackEventWithProperties("sharePromptShown", { moment });
};

export const init = () => {
  stores.streak.events.on("goalMet", () => {
    if (stores.streak.days === STREAK_DAYS) {
      pendingStreakMoment = true;
    }
  });
};

/** A thumbs-up on the end-of-text "Did you like this…?" vote */
export const upvoted = () => showAtDelightMoment("upvote");

/** Landing on the Learn page after finishing a practice session */
export const practiceCompleted = () => showAtDelightMoment("practice");

/**
 * Called on every page render. Shows a held streak moment once off the
 * reader, and otherwise the timer modal, except to the experiment arm.
 */
export const pageRendered = async (pageName: string) => {
  if (pendingStreakMoment && pageName !== "readerPage") {
    pendingStreakMoment = false;
    await showAtDelightMoment("streak");
  }
  if (
    !deviceDetect.isIOSApp() &&
    !stores.user.hasPremiumAccess.value &&
    upsellTimer.isReady() &&
    !(isInExperimentPopulation() && isExperimentArm())
  ) {
    modalController.show(<SocialShareDialog />, {
      title: "upsellTimer",
      cancelCallback: () => upsellTimer.reset(),
    });
  }
};

export const _functionsForUnitTests = {
  reset: () => {
    pendingStreakMoment = false;
  },
};
